import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/ui/Modal';
import { formatPhone } from './onlineOrderFormat';
import ReservationCalendar from './ReservationCalendar';
import ReservationsFilter from './ReservationsFilter';
import {
  DEFAULT_RESERVATION_FILTER,
  canMarkNoShow,
  filterReservations,
  reservationFilterCounts,
  reservationTiming,
  reservationUndoTarget,
  shiftDay,
  totalCovers,
  type ReservationFilterKey,
  type ReservationTiming,
} from './reservationList';
import { sortWaiterTables } from './tableSort';
import { formatTicketTime } from './ticketTime';
import type { WaiterReservation, WaiterReservationStatus, WaiterTable } from './waiterApi';

interface Props {
  /** SC-970 — the WHOLE day, every status, straight from the server: the panel
   *  owns the filter bar. The caller still owns the clock, because the tab
   *  badge counts the « À venir » bucket. */
  reservations: WaiterReservation[];
  nowMs: number;
  /** Minutes a booking stays in « À venir » past its time (a late guest). */
  graceMinutes: number;
  loadError: boolean;
  /** DF-0226 — `fulfilledTimeStamp` de la requête : l'heure de la dernière
   *  réponse reçue, pas celle du dernier rendu. `undefined` tant qu'aucune
   *  réponse n'est arrivée. */
  updatedAtMs?: number;
  /** DF-0226 — redemande la liste tout de suite. */
  onRefresh: () => void;
  tables: WaiterTable[];
  /** Arrivé / Pas venu / Annuler from the sheet. Resolves to an error message to show
   *  in the sheet, or `null` once hub accepted the move. */
  onSetStatus: (reservation: WaiterReservation, status: WaiterReservationStatus) => Promise<string | null>;
  /** « Installer » — hand the chosen free table to the page's open-table
   *  modal, which marks the booking arrived once the table is open. */
  onSeat: (reservation: WaiterReservation, table: WaiterTable) => void;
  /** SC-1281 — « Modifier » : la page ouvre le formulaire pré-rempli. Le
   *  panneau ferme sa fiche avant, pour ne pas empiler deux feuilles. */
  onEdit: (reservation: WaiterReservation) => void;
  /** SC-1532 — « Imprimer » : ressort le ticket de la réservation. Résout vers
   *  ce qu'il faut dire sous le bouton — `ok` vrai si le ticket est parti. La
   *  fiche reste ouverte, d'où pas de toast : il la recouvrirait sur un petit
   *  téléphone. */
  onPrint: (reservation: WaiterReservation) => Promise<{ ok: boolean; message: string }>;
  /** SC-1612 — le jour listé (`YYYY-MM-DD`, heure du restaurant) et celui
   *  d'aujourd'hui, tels que hub les a donnés. Sur un autre jour, la fiche ne
   *  garde que ce qui y a un sens : Modifier, Appeler, Imprimer — hub refuse
   *  les gestes de salle hors du jour même (NOT_TODAY). */
  day?: string;
  today?: string;
  /** Aller à un autre jour ; `null` = revenir à aujourd'hui. */
  onChangeDay?: (day: string | null) => void;
  /** Jeton serveur : le calendrier demande lui-même ses comptes du mois. */
  token?: string;
  /** La réponse du jour demandé n'est pas encore là. */
  loading?: boolean;
}

type SheetView = 'detail' | 'tables' | 'noShow' | 'cancel';

/** SC-970 — how each status reads on a row and in the sheet. `--w-info` stays
 *  the confirmed colour the rows already wore. */
const STATUS_STYLE: Record<string, { labelKey: string; bg: string; fg: string }> = {
  CONFIRMED: { labelKey: 'waiter.notes.reservations.statusConfirmed', bg: 'var(--w-info)', fg: '#fff' },
  COMPLETED: { labelKey: 'waiter.notes.reservations.statusArrived', bg: 'var(--w-success)', fg: '#fff' },
  NO_SHOW: { labelKey: 'waiter.notes.reservations.statusNoShow', bg: 'var(--w-danger)', fg: '#fff' },
  CANCELLED: {
    labelKey: 'waiter.notes.reservations.statusCancelled',
    bg: 'var(--w-pending-bg)',
    fg: 'var(--w-pending-fg)',
  },
};

function StatusBadge({ reservation }: { reservation: WaiterReservation }) {
  const { t } = useTranslation();
  const style = STATUS_STYLE[reservation.status];
  if (!style) return null;
  return (
    <span
      data-testid="reservation-status"
      data-status={reservation.status}
      className="shrink-0 rounded-full px-2 py-0.5 font-sans text-sm font-bold"
      style={{ backgroundColor: style.bg, color: style.fg }}
    >
      {t(style.labelKey)}
    </span>
  );
}

function TimingBadge({ timing }: { timing: ReservationTiming }) {
  const { t } = useTranslation();
  if (timing.kind === 'later') return null;
  if (timing.kind === 'late') {
    return (
      <span
        data-testid="reservation-timing"
        data-kind="late"
        className="shrink-0 rounded-full px-2 py-0.5 font-sans text-sm font-bold text-white"
        style={{ backgroundColor: 'var(--w-danger)' }}
      >
        {t('waiter.notes.reservations.late', { n: timing.minutes })}
      </span>
    );
  }
  return (
    <span
      data-testid="reservation-timing"
      data-kind={timing.kind}
      className="shrink-0 rounded-full px-2 py-0.5 font-sans text-sm font-semibold"
      style={{ backgroundColor: 'var(--w-pending-bg)', color: 'var(--w-pending-fg)' }}
    >
      {timing.kind === 'now'
        ? t('waiter.notes.reservations.now')
        : t('waiter.notes.reservations.inMinutes', { n: timing.minutes })}
    </span>
  );
}

/** « jeu. 5 nov. » — the date string is a calendar day, not an instant. */
function formatDay(day: string, language: string): string {
  return new Date(`${day}T12:00:00Z`).toLocaleDateString(language, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

/**
 * SC-1612 — ‹ jour › au-dessus du résumé : le client qui a réservé pour la
 * semaine prochaine rappelle, le serveur va à ce jour-là. La date se touche
 * pour ouvrir le calendrier du carnet, qui montre combien de réservations
 * attend chaque jour ; « Aujourd'hui » ramène au service.
 */
function DayBar({
  day,
  today,
  onChange,
  onOpenCalendar,
}: {
  day: string;
  today: string;
  onChange: (day: string | null) => void;
  onOpenCalendar: () => void;
}) {
  const { t, i18n } = useTranslation();
  const isToday = day === today;
  const go = (next: string) => onChange(next === today ? null : next);
  const ARROW =
    'shrink-0 w-11 h-11 flex items-center justify-center rounded-full border-2 border-ink bg-paper font-sans text-xl font-bold text-ink';
  return (
    <div data-testid="reservations-day" className="flex items-center gap-2 px-3 pt-3">
      <button
        type="button"
        data-testid="reservations-day-prev"
        aria-label={t('waiter.notes.reservations.dayPrev')}
        onClick={() => go(shiftDay(day, -1))}
        className={ARROW}
      >
        <span aria-hidden="true" className="rtl:-scale-x-100">‹</span>
      </button>
      <button
        type="button"
        data-testid="reservations-day-open"
        aria-label={t('waiter.notes.reservations.dayPick')}
        onClick={onOpenCalendar}
        className="min-w-0 flex-1 h-11 flex items-center justify-center gap-1.5 rounded-full bg-paper border border-hairline font-sans text-base font-bold text-ink"
      >
        <span aria-hidden="true">📅</span>
        <span data-testid="reservations-day-label" className="truncate">
          {isToday
            ? `${t('waiter.notes.reservations.today')} · ${formatDay(day, i18n.language)}`
            : formatDay(day, i18n.language)}
        </span>
      </button>
      <button
        type="button"
        data-testid="reservations-day-next"
        aria-label={t('waiter.notes.reservations.dayNext')}
        onClick={() => go(shiftDay(day, 1))}
        className={ARROW}
      >
        <span aria-hidden="true" className="rtl:-scale-x-100">›</span>
      </button>
      {!isToday && (
        <button
          type="button"
          data-testid="reservations-day-today"
          onClick={() => onChange(null)}
          className="shrink-0 h-11 px-3 rounded-full bg-ink text-paper font-sans text-sm font-bold"
        >
          {t('waiter.notes.reservations.today')}
        </button>
      )}
    </div>
  );
}

/**
 * SC-966 — the « Résas » tab: today's confirmed bookings, nearest first, each
 * leaving the list 30 min after its time (the caller filters). Tapping a row
 * opens a sheet with the three things a waiter does with a booking at the
 * door: mark the guest arrived, seat them at a free table (which marks them
 * arrived too), cancel the booking, or — once the time has come — mark
 * them as a no-show.
 */
export default function ReservationsPanel({
  reservations,
  nowMs,
  graceMinutes,
  loadError,
  updatedAtMs,
  onRefresh,
  tables,
  onSetStatus,
  onSeat,
  onEdit,
  onPrint,
  day,
  today,
  onChangeDay,
  token,
  loading = false,
}: Props) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ReservationFilterKey>(DEFAULT_RESERVATION_FILTER);
  const [target, setTarget] = useState<WaiterReservation | null>(null);
  const [view, setView] = useState<SheetView>('detail');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // SC-1532 — dit sous « Imprimer », pas en tête de fiche : le bouton est en
  // bas, un message en haut tomberait hors de vue sur un petit écran.
  // SC-1612 — hors de la barre collante : une feuille dans un `sticky z-10`
  // passerait sous l'en-tête du carnet.
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [printOutcome, setPrintOutcome] = useState<{ ok: boolean; message: string } | null>(null);
  const printOutcomeRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    // La fiche atteint sa hauteur maximale sur un 360 × 640 : sans ça, la fin
    // du message reste sous le bord.
    if (printOutcome) printOutcomeRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [printOutcome]);

  const counts = useMemo(
    () => reservationFilterCounts(reservations, nowMs, graceMinutes),
    [reservations, nowMs, graceMinutes],
  );
  const rows = useMemo(
    () => filterReservations(reservations, filter, nowMs, graceMinutes),
    [reservations, filter, nowMs, graceMinutes],
  );

  // SC-1612 — sans jour connu (vieux serveur), c'est aujourd'hui.
  const isToday = !day || !today || day === today;
  const next = isToday ? rows.find((r) => Date.parse(r.reservedFor) >= nowMs) ?? null : null;

  const openSheet = (reservation: WaiterReservation) => {
    setTarget(reservation);
    setView('detail');
    setError(null);
    setPrintOutcome(null);
    setBusy(false);
  };
  const closeSheet = () => {
    if (busy) return;
    setTarget(null);
    setBusy(false);
  };

  const apply = async (status: WaiterReservationStatus) => {
    if (!target || busy) return;
    setBusy(true);
    setError(null);
    const message = await onSetStatus(target, status);
    if (message === null) {
      setTarget(null);
      setBusy(false);
    } else {
      setError(message);
      setBusy(false);
      setView('detail');
    }
  };

  // SC-1532 — la fiche reste ouverte : imprimer ne change rien à la réservation,
  // le serveur enchaîne souvent avec « Installer ».
  const print = async () => {
    if (!target || busy) return;
    setBusy(true);
    setPrintOutcome(null);
    setPrintOutcome(await onPrint(target));
    setBusy(false);
  };

  // Free tables only; the ones that seat the whole party first, each group in
  // the room order the Sur place grid uses.
  const seatable = useMemo(() => {
    if (!target) return [];
    const free = sortWaiterTables(
      tables.filter((tb) => tb.session === null),
      'room',
    );
    const fits = free.filter((tb) => tb.maxCovers >= target.partySize);
    const tooSmall = free.filter((tb) => tb.maxCovers < target.partySize);
    return [...fits, ...tooSmall];
  }, [tables, target]);

  const noShowAllowed = target ? canMarkNoShow(target.reservedFor, nowMs) : false;
  const canUndo = target?.status === 'COMPLETED' || target?.status === 'NO_SHOW';
  const undoTo: WaiterReservationStatus = target ? reservationUndoTarget(target) : 'CONFIRMED';

  return (
    <>
      <div className="sticky top-0 z-10 bg-cream">
      {day && today && onChangeDay && (
        <DayBar day={day} today={today} onChange={onChangeDay} onOpenCalendar={() => setCalendarOpen(true)} />
      )}
      <div
        data-testid="reservations-summary"
        className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-3 pt-3 pb-2 border-b border-hairline"
      >
        <h2 className="font-sans text-base font-bold text-ink">
          {t('waiter.notes.reservations.summary', {
            n: rows.length,
            covers: totalCovers(rows),
          })}
        </h2>
        {next && (
          <span data-testid="reservations-next" className="font-sans text-sm text-ink-mute">
            {t('waiter.notes.reservations.next', { time: formatTicketTime(next.reservedFor) })}
          </span>
        )}
        {/* DF-0226 — « Aucune réservation » se lit comme un fait vérifié. Cet
            écran doit dire de QUAND il parle, et laisser redemander : les
            réservations sont la seule donnée du carnet que rien ne pousse. */}
        <button
          type="button"
          data-testid="reservations-refresh"
          onClick={onRefresh}
          aria-label={t('waiter.notes.reservations.refresh')}
          className="ml-auto shrink-0 rounded-full border border-hairline px-3 py-1 font-sans text-sm text-ink-mute"
        >
          {updatedAtMs
            ? t('waiter.notes.reservations.updatedAt', {
                time: formatTicketTime(new Date(updatedAtMs).toISOString()),
              })
            : t('waiter.notes.reservations.refresh')}
        </button>
      </div>

      <ReservationsFilter counts={counts} active={filter} onSelect={setFilter} />
      </div>

      {loadError && (
        <p
          data-testid="reservations-error"
          role="alert"
          className="px-3 pt-2 font-sans text-sm font-semibold"
          style={{ color: 'var(--w-danger)' }}
        >
          {t('waiter.notes.reservations.loadError')}
        </p>
      )}

      {loading ? (
        <p data-testid="reservations-loading" className="px-3 py-6 font-sans text-base text-ink-mute text-center">
          {t('waiter.notes.reservations.loadingDay')}
        </p>
      ) : rows.length === 0 ? (
        <p data-testid="reservations-empty" className="px-3 py-6 font-sans text-base text-ink-mute text-center">
          {t(
            filter !== 'upcoming'
              ? 'waiter.notes.reservations.emptyFiltered'
              : isToday
                ? 'waiter.notes.reservations.empty'
                : 'waiter.notes.reservations.emptyOtherDay',
          )}
        </p>
      ) : (
        <div data-testid="reservations-list" className="flex flex-col">
          {rows.map((r) => {
            const timing = reservationTiming(r.reservedFor, nowMs);
            const phone = formatPhone(r.customerPhone);
            return (
              // Same wrapper-not-button shape as `OnlineOrderRow`: the phone
              // is a real `tel:` link lifted above a row-wide open button.
              <div
                key={r.uuid}
                data-testid={`reservation-row-${r.uuid}`}
                data-late={timing.kind === 'late'}
                style={{
                  borderInlineStart: `4px solid ${STATUS_STYLE[r.status]?.bg ?? 'var(--w-info)'}`,
                }}
                className="relative w-full min-h-11 flex flex-col gap-1 px-3 py-3 border-b border-hairline bg-paper text-start"
              >
                <button
                  type="button"
                  data-testid={`reservation-open-${r.uuid}`}
                  aria-label={r.customerName}
                  onClick={() => openSheet(r)}
                  className="absolute inset-0 w-full h-full"
                />
                <span className="flex items-center gap-2">
                  <span
                    className="shrink-0 font-sans text-[18px] font-bold tabular-nums"
                    style={timing.kind === 'late' ? { color: 'var(--w-danger)' } : undefined}
                  >
                    {formatTicketTime(r.reservedFor)}
                  </span>
                  <span className="min-w-0 flex-1 font-sans text-[18px] font-semibold truncate">{r.customerName}</span>
                  <span
                    data-testid="reservation-party"
                    className="shrink-0 rounded-full border-2 border-ink px-2 py-0.5 font-sans text-sm font-bold text-ink"
                  >
                    {t('waiter.notes.reservations.party', { n: r.partySize })}
                  </span>
                </span>
                <span className="flex items-center gap-1.5 flex-wrap min-w-0">
                  <StatusBadge reservation={r} />
                  {/* Le compte à rebours ne parle que d'une réservation encore
                      attendue : « en retard de 240 min » sur une cliente
                      arrivée à midi n'est qu'un bruit. */}
                  {r.status === 'CONFIRMED' && <TimingBadge timing={timing} />}
                  {r.status !== 'CANCELLED' && r.cancelledAt && (
                    <span
                      data-testid="reservation-was-cancelled"
                      className="shrink-0 font-sans text-sm italic text-ink-mute"
                    >
                      {t('waiter.notes.reservations.wasCancelled')}
                    </span>
                  )}
                  {phone && (
                    <a
                      href={`tel:${r.customerPhone.replace(/\s+/g, '')}`}
                      className="relative font-sans text-base text-ink underline underline-offset-2 py-1 -my-1"
                    >
                      {phone}
                    </a>
                  )}
                  {r.notes && (
                    <span
                      data-testid="reservation-row-note"
                      className="min-w-0 basis-full font-sans text-sm italic text-ink-mute truncate"
                    >
                      {r.notes}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {day && today && onChangeDay && token && (
        <ReservationCalendar
          open={calendarOpen}
          onClose={() => setCalendarOpen(false)}
          token={token}
          day={day}
          today={today}
          onPick={(picked) => onChangeDay(picked === today ? null : picked)}
        />
      )}

      <Modal
        open={target !== null}
        onClose={closeSheet}
        ariaLabel={target ? t('waiter.notes.reservations.sheetTitle', { name: target.customerName }) : ''}
        instant
      >
        {target && (
          <div data-testid="reservation-sheet" className="p-4 flex flex-col gap-3 min-h-0 overflow-y-auto">
            <div className="flex items-start justify-between gap-2">
              <h2 className="font-sans text-[18px] font-bold min-w-0 break-words">
                {formatTicketTime(target.reservedFor)} · {target.customerName}
              </h2>
              <button
                type="button"
                data-testid="reservation-close"
                aria-label={t('common.close')}
                disabled={busy}
                onClick={closeSheet}
                className="-me-2 -mt-2 w-11 h-11 shrink-0 flex items-center justify-center font-sans text-2xl leading-none text-ink"
              >
                ×
              </button>
            </div>
            {/* SC-1281 — « Modifier » vit à côté de ce qu'il modifie (personnes,
                heure), pas dans la pile des gestes de salle : la fiche garde
                ses rangées de boutons d'avant, et ne s'allonge pas. Seulement
                tant qu'elle est confirmée — une table arrivée ou annulée se
                corrige d'abord par son marquage. */}
            <div className="flex items-start justify-between gap-3">
              <p className="min-w-0 flex flex-wrap items-center gap-2 pt-2.5 font-sans text-base">
                <span className="font-bold">{t('waiter.notes.reservations.party', { n: target.partySize })}</span>
                <StatusBadge reservation={target} />
                {target.status === 'CONFIRMED' && (
                  <TimingBadge timing={reservationTiming(target.reservedFor, nowMs)} />
                )}
              </p>
              {target.status === 'CONFIRMED' && (
                <button
                  type="button"
                  data-testid="reservation-edit"
                  disabled={busy}
                  onClick={() => {
                    const reservation = target;
                    setTarget(null);
                    onEdit(reservation);
                  }}
                  className="shrink-0 h-11 px-4 rounded-btn border-2 border-ink bg-paper font-sans text-base font-bold text-ink disabled:opacity-50"
                >
                  <span aria-hidden="true">✎ </span>
                  {t('waiter.notes.reservations.edit')}
                </button>
              )}
            </div>
            {target.status === 'CANCELLED' && (
              <p data-testid="reservation-cancelled-note" className="font-sans text-base text-ink-mute">
                {t('waiter.notes.reservations.cancelledNote')}
              </p>
            )}
            {target.notes && (
              <p data-testid="reservation-sheet-note" className="font-sans text-base bg-ash rounded-md p-2 break-words">
                {target.notes}
              </p>
            )}
            {error && (
              <p data-testid="reservation-sheet-error" role="alert" className="font-sans text-base font-semibold" style={{ color: 'var(--w-danger)' }}>
                {error}
              </p>
            )}

            {view === 'detail' && !isToday && (
              <p data-testid="reservation-other-day-hint" className="font-sans text-sm text-ink-mute">
                {t('waiter.notes.reservations.otherDayHint')}
              </p>
            )}
            {view === 'detail' && (
              <>
                {isToday && (
                  <button
                    type="button"
                    data-testid="reservation-seat"
                    disabled={busy}
                    onClick={() => setView('tables')}
                    className="shrink-0 h-14 rounded-btn bg-ink text-paper font-sans text-[18px] font-bold disabled:opacity-50"
                  >
                    {t('waiter.notes.reservations.seat')}
                  </button>
                )}
                {isToday && (
                  <div className="grid grid-cols-2 gap-2">
                    {target.status !== 'COMPLETED' && (
                      <button
                        type="button"
                        data-testid="reservation-arrived"
                        disabled={busy}
                        aria-busy={busy}
                        onClick={() => void apply('COMPLETED')}
                        className="h-12 rounded-btn text-white font-sans text-base font-bold disabled:opacity-50"
                        style={{ backgroundColor: 'var(--w-success)' }}
                      >
                        {t('waiter.notes.reservations.arrived')}
                      </button>
                    )}
                    {/* Une réservation annulée n'est pas une cliente qui n'est
                        pas venue : « Pas venu » n'a rien à y dire. */}
                    {target.status !== 'NO_SHOW' && target.status !== 'CANCELLED' && (
                      <button
                        type="button"
                        data-testid="reservation-no-show"
                        disabled={busy || !noShowAllowed}
                        onClick={() => setView('noShow')}
                        className="h-12 rounded-btn border-2 font-sans text-base font-bold bg-paper disabled:opacity-40"
                        style={{ borderColor: 'var(--w-danger)', color: 'var(--w-danger)' }}
                      >
                        {t('waiter.notes.reservations.noShow')}
                      </button>
                    )}
                  </div>
                )}
                {/* SC-970 — « revenir en arrière » sur un marquage fait par
                    erreur. La cible est dictée par `cancelledAt`, jamais
                    choisie ici : une annulation n'est jamais effacée
                    par la correction d'un marquage. */}
                {isToday && canUndo && (
                  <button
                    type="button"
                    data-testid="reservation-undo"
                    data-undo-target={undoTo}
                    disabled={busy}
                    aria-busy={busy}
                    onClick={() => void apply(undoTo)}
                    className="h-12 rounded-btn border-2 border-ink bg-paper font-sans text-base font-bold text-ink disabled:opacity-50"
                  >
                    {t(
                      undoTo === 'CANCELLED'
                        ? 'waiter.notes.reservations.undoToCancelled'
                        : 'waiter.notes.reservations.undoToConfirmed',
                    )}
                  </button>
                )}
                {isToday && target.status !== 'NO_SHOW' && target.status !== 'CANCELLED' && !noShowAllowed && (
                  <p data-testid="reservation-no-show-hint" className="font-sans text-sm text-ink-mute">
                    {t('waiter.notes.reservations.noShowFrom', { time: formatTicketTime(target.reservedFor) })}
                  </p>
                )}
                {isToday && target.status === 'CONFIRMED' && (
                  <button
                    type="button"
                    data-testid="reservation-cancel"
                    disabled={busy}
                    onClick={() => setView('cancel')}
                    className="shrink-0 h-12 rounded-btn border-2 font-sans text-base font-bold bg-paper disabled:opacity-40"
                    style={{ borderColor: 'var(--w-danger)', color: 'var(--w-danger)' }}
                  >
                    {t('waiter.notes.reservations.cancel')}
                  </button>
                )}
                {target.customerPhone.trim() && (
                  <a
                    data-testid="reservation-call"
                    href={`tel:${target.customerPhone.replace(/\s+/g, '')}`}
                    className="shrink-0 h-12 rounded-btn border-2 border-ink bg-paper font-sans text-base font-bold text-ink flex items-center justify-center gap-2"
                  >
                    {t('waiter.notes.reservations.call', { phone: formatPhone(target.customerPhone) })}
                  </a>
                )}
                {/* SC-1532 — « Imprimer » en dernier, plus petit, sous un filet :
                    un geste rare, qui ne prend la place d'aucun bouton que les
                    serveurs ont dans les doigts (« Modifier » reste en tête).
                    Seulement dans la vue détail, jamais pendant le choix d'une
                    table. Pas pour une réservation annulée ou pas venue : le
                    papier annoncerait une table qui ne viendra pas. */}
                {(target.status === 'CONFIRMED' || target.status === 'COMPLETED') && (
                  <div className="mt-1 pt-3 border-t border-hairline flex flex-col items-center gap-2">
                    <button
                      type="button"
                      data-testid="reservation-print"
                      disabled={busy}
                      aria-busy={busy}
                      onClick={() => void print()}
                      className="h-11 px-5 rounded-btn border-2 border-ink bg-paper font-sans text-base font-bold text-ink disabled:opacity-50"
                    >
                      <span aria-hidden="true">🖨 </span>
                      {t('waiter.notes.reservations.print')}
                    </button>
                    {printOutcome && (
                      <p
                        ref={printOutcomeRef}
                        data-testid={printOutcome.ok ? 'reservation-print-done' : 'reservation-print-error'}
                        role={printOutcome.ok ? 'status' : 'alert'}
                        className="font-sans text-base font-semibold text-center"
                        style={{ color: printOutcome.ok ? 'var(--w-success)' : 'var(--w-danger)' }}
                      >
                        {printOutcome.message}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}

            {view === 'tables' && (
              <>
                <h3 className="font-sans text-base font-bold border-t-2 border-hairline pt-3">
                  {t('waiter.notes.reservations.pickTable')}
                </h3>
                {seatable.length === 0 ? (
                  <p data-testid="reservation-no-free-table" className="font-sans text-base text-ink-mute">
                    {t('waiter.overview.transferNoFreeTables')}
                  </p>
                ) : (
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(72px,1fr))] gap-2 max-h-[45dvh] overflow-y-auto">
                    {seatable.map((tb) => {
                      const fits = tb.maxCovers >= target.partySize;
                      return (
                        <button
                          key={tb.uuid}
                          type="button"
                          data-testid={`reservation-table-${tb.uuid}`}
                          data-fits={fits}
                          aria-label={t('waiter.ticket.header.tableLabel', { table: tb.number })}
                          onClick={() => {
                            const reservation = target;
                            closeSheet();
                            onSeat(reservation, tb);
                          }}
                          className={`h-14 border-2 rounded-btn px-1 flex flex-col items-center justify-center leading-tight ${
                            fits ? 'bg-paper border-ink text-ink' : 'bg-ash border-hairline text-ink-mute'
                          }`}
                        >
                          <span className="font-sans text-[18px] font-bold truncate max-w-full">{tb.number}</span>
                          <span className="font-sans text-xs">
                            {t('waiter.notes.reservations.seats', { n: tb.capacity })}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
                <button
                  type="button"
                  data-testid="reservation-tables-back"
                  onClick={() => setView('detail')}
                  className="shrink-0 h-12 rounded-btn border-2 border-ink bg-paper font-sans text-base font-bold text-ink"
                >
                  {t('waiter.notes.reservations.back')}
                </button>
              </>
            )}

            {(view === 'noShow' || view === 'cancel') && (
              <div data-testid={view === 'cancel' ? 'reservation-cancel-confirm' : 'reservation-no-show-confirm'} className="flex flex-col gap-3 border-t-2 border-hairline pt-3">
                <p className="font-sans text-base">
                  {t(view === 'cancel' ? 'waiter.notes.reservations.cancelConfirm' : 'waiter.notes.reservations.noShowConfirm', { name: target.customerName })}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid={view === 'cancel' ? 'reservation-cancel-back' : 'reservation-no-show-cancel'}
                    disabled={busy}
                    onClick={() => setView('detail')}
                    className="flex-1 h-12 bg-paper border-2 border-ink rounded-btn px-4 font-sans text-base font-bold text-ink disabled:opacity-50"
                  >
                    {t(view === 'cancel' ? 'waiter.notes.reservations.back' : 'common.cancel')}
                  </button>
                  <button
                    type="button"
                    data-testid={view === 'cancel' ? 'reservation-cancel-ok' : 'reservation-no-show-ok'}
                    disabled={busy}
                    aria-busy={busy}
                    onClick={() => void apply(view === 'cancel' ? 'CANCELLED' : 'NO_SHOW')}
                    className="flex-1 h-12 rounded-btn px-4 font-sans text-base font-bold text-white disabled:opacity-50"
                    style={{ backgroundColor: 'var(--w-danger)' }}
                  >
                    {t(view === 'cancel' ? 'waiter.notes.reservations.cancelOk' : 'waiter.notes.reservations.noShowOk')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
